// ── Migration Review Flag Resolver ──
// Pure function: given a candidate Job (post-mapping, pre-approval),
// returns the set of review flags an operator must clear/acknowledge
// before the job can be approved into the live operational lifecycle.
//
// Re-run on every edit in the Migration Review Queue. Approval is
// blocked while any flag in BLOCKING_FLAGS is present.
//
// Anchor: mem://features/jobber-import-bridge (beta)

import type { Job, ReviewFlag } from "../types";

/** Flags that hard-block the Approve action in the Review Queue. */
export const BLOCKING_FLAGS: ReadonlySet<ReviewFlag> = new Set<ReviewFlag>([
  "missing_address",
  "missing_scope",
  "missing_install_type",
]);

/** Flags that are warnings only — Approve allowed, but surfaced. */
export const WARNING_FLAGS: ReadonlySet<ReviewFlag> = new Set<ReviewFlag>([
  "missing_schedule",
  "missing_offer_comp",
  "missing_photos_spec",
  "ambiguous_line_items",
  "low_confidence_mapping",
  "duplicate_possible",
]);

export function computeReviewFlags(job: Partial<Job>): ReviewFlag[] {
  const flags: ReviewFlag[] = [];

  const addr = job.address;
  const addrOk =
    !!addr &&
    typeof addr.street === "string" &&
    addr.street.trim().length > 0 &&
    typeof addr.city === "string" &&
    addr.city.trim().length > 0;
  if (!addrOk) flags.push("missing_address");

  if (!job.scopeSummary || job.scopeSummary.trim().length < 4) {
    flags.push("missing_scope");
  }

  if (!job.projectType || (job.projectType as string) === "") {
    flags.push("missing_install_type");
  }

  if (!job.scheduledInstallDate || job.scheduledInstallDate.trim() === "") {
    flags.push("missing_schedule");
  }

  const contract = job.financials?.contractValue;
  if (typeof contract !== "number" || contract <= 0) {
    flags.push("missing_offer_comp");
  }

  const hasPhoto = Array.isArray(job.photos) && job.photos.length > 0;
  const hasSpec = Array.isArray(job.materialsList) && job.materialsList.length > 0;
  if (!hasPhoto && !hasSpec) {
    flags.push("missing_photos_spec");
  }

  if (Array.isArray(job.materialsList)) {
    const ambiguous = job.materialsList.some(
      (m) => !m.sku || m.sku.trim() === "" || !m.itemName || m.itemName.trim() === "",
    );
    if (ambiguous) flags.push("ambiguous_line_items");
  }

  const conf = job.sourceMeta?.mappingConfidence;
  if (typeof conf === "number" && conf < 0.6) {
    flags.push("low_confidence_mapping");
  }

  return flags;
}

export function hasBlockingFlag(flags: ReadonlyArray<ReviewFlag>): boolean {
  return flags.some((f) => BLOCKING_FLAGS.has(f));
}
